// Общие типы, шарятся между сервером и клиентом.

export type Role = 'admin' | 'member';

export interface User {
  id: string;
  email: string;
  name: string;
  role: Role;
  avatarColor: string;
  suspended: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface AuthResponse {
  token: string;
  user: User;
}

export interface TwoFactorStatus {
  code: string;
  msRemaining: number;
  periodSeconds: number;
  /** 2FA выключена кнопкой в админке — сотрудники входят по одному паролю. */
  disabled: boolean;
}

// -------- Справочники --------

/** Порядок = жизненный цикл: выдан под контейнер → контейнер создан →
 *  залогинен → прогрев → работа → хьюман → архив. */
export const ACCOUNT_STATUSES = [
  'Pending', 'Контейнер создан', 'Залогинен', 'Прогрев', 'В работе', 'Хьюман', 'Бан', 'Выключение',
] as const;
export type AccountStatus = (typeof ACCOUNT_STATUSES)[number];

/**
 * Архивные статусы: такие акки уходят из рабочих таблиц в раздел «Архив».
 * Это ТОЛЬКО представление — аккаунт остаётся привязан к телефону и модели,
 * его снапшоты и просмотры продолжают учитываться в графиках и агрегатах
 * (нужны для конверта просмотров в фанов и прочей аналитики).
 */
export const ARCHIVED_STATUSES = ['Бан', 'Выключение'] as const;

/** Активные статусы — то, что показывается в рабочих таблицах. */
export const ACTIVE_STATUSES = ['Pending', 'Контейнер создан', 'Залогинен', 'Прогрев', 'В работе', 'Хьюман'] as const;

export function isArchivedStatus(status: string | null | undefined): boolean {
  return status === 'Бан' || status === 'Выключение';
}

/** Подсказки для поля «Тип аккаунта» (само поле — свободный текст). */
export const ACCOUNT_TYPES = [
  'Самореги Прокси',
  'Покупные Прокси',
  'Покупные Сим',
  'Самореги Сим',
  'Покупные Сим DE',
  'Старые Акки',
] as const;

// -------- Телефоны / модели --------

export interface PhoneSummary {
  id: string;
  name: string;
  /** Серийный номер железки — идентификатор для автопостера/капчера. */
  serial: string | null;
  note: string | null;
  accountCount: number;
  /** Кол-во акков по каждому статусу (ключ — статус, отсутствует = 0). */
  byStatus: Record<string, number>;
  views24h: number;
  viewsTotal: number;
  /** Самый свежий парсинг среди акков — «обновлено» рядом с просмотрами. */
  lastParsedAt: string | null;
  createdAt: string;
}

export interface ModelSummary {
  id: string;
  name: string;
  accountCount: number;
  byStatus: Record<string, number>;
  /** Для воронки: считаются по ВСЕМ аккам модели, включая архивные. */
  decoratedCount: number;
  pouredCount: number;
  views24h: number;
  viewsTotal: number;
  lastParsedAt: string | null;
  createdAt: string;
}

// -------- Аккаунты --------

/**
 * Один инста-аккаунт. Креды (password/totpSecret/email/emailPassword) сервер
 * отдаёт ТОЛЬКО админам — для member эти поля приходят null, а hasCreds
 * говорит «заполнено, но скрыто».
 */
export interface Account {
  id: string;
  phoneId: string;
  phoneName: string;
  modelId: string | null;
  modelName: string | null;

  containerId: number | null;
  username: string;
  igUrl: string | null;

  password: string | null;
  totpSecret: string | null;
  email: string | null;
  emailPassword: string | null;
  /** Есть ли скрытые креды (для member, у которого поля обнулены). */
  hasCreds: boolean;

  /** Страна на русском (по ней подбирается координата) и язык контейнера. */
  country: string | null;
  language: string | null;
  /** Координата, выданная при создании контейнера («lat, lng»). */
  geo: string | null;

  accountType: string | null;
  batch: string | null;
  cloneVersion: string | null;
  creoName: string | null;

  status: AccountStatus | null;
  warn: boolean;
  /** Варн за 18+ контент — отдельный флаг, ставится парсером или руками. */
  warn18: boolean;
  decorated: boolean;
  poured: boolean;

  link: string | null;
  startDate: string | null; // YYYY-MM-DD
  banDate: string | null;   // YYYY-MM-DD

  viewsCategory: string | null;
  views24h: number;
  videos24h: number;
  viewsTotal: number;

  containerReplacements: number;
  creoReplaced: boolean;
  creoReplaceReason: string | null;

  note: string | null;
  /** Отработка автопостера «выложено/план», например «1/3»; null — нет данных. */
  dayWorkout: string | null;
  nightWorkout: string | null;
  /** Акк «необработавший»: одна из отработок неполная и крестиком не скрыт. */
  unworked: boolean;
  /** Кол-во снесённых инстой рилсов (deletedAt != null). */
  deletedReels: number;
  /** Рилсов, опубликованных сегодня (календарный день Варшавы). */
  postedToday: number;
  /** Удалённых инстой рилсов из опубликованных сегодня. */
  deletedToday: number;
  /** Когда акк ушёл в архив (последняя смена статуса на Бан/Выключение). */
  archivedAt: string | null;
  lastParsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Поля Account, которые можно править через PATCH /api/accounts/:id. */
export type AccountPatch = Partial<
  Pick<
    Account,
    | 'phoneId'
    | 'modelId'
    | 'containerId'
    | 'username'
    | 'igUrl'
    | 'password'
    | 'totpSecret'
    | 'email'
    | 'emailPassword'
    | 'country'
    | 'language'
    | 'accountType'
    | 'batch'
    | 'cloneVersion'
    | 'creoName'
    | 'status'
    | 'warn'
    | 'warn18'
    | 'decorated'
    | 'poured'
    | 'link'
    | 'startDate'
    | 'banDate'
    | 'viewsCategory'
    | 'views24h'
    | 'videos24h'
    | 'viewsTotal'
    | 'containerReplacements'
    | 'creoReplaced'
    | 'creoReplaceReason'
    | 'note'
  >
>;

export interface AccountEvent {
  id: string;
  accountId: string;
  username: string;
  userName: string | null;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  createdAt: string;
}

export interface AccountSnapshot {
  date: string; // YYYY-MM-DD
  views24h: number;
  videos24h: number;
  viewsTotal: number;
}

export interface Reel {
  id: string;
  accountId: string;
  url: string;
  caption: string | null;
  publishedAt: string | null; // YYYY-MM-DD
  views: number;
  likes: number;
  comments: number;
  /** Рилс снесён инстой (не пришёл в полном списке от парсера); метрики заморожены. */
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccountDetail {
  account: Account;
  events: AccountEvent[];
  snapshots: AccountSnapshot[];
  reels: Reel[];
}

// -------- Bulk-импорт аккаунтов (CSV/текстовик) --------

/** Одна строка bulk-импорта. Телефон/модель можно передать именем —
 *  телефон должен существовать, модель создастся при отсутствии. */
export interface BulkAccountInput extends AccountPatch {
  username: string;
  phoneName?: string;
  /** Серийник телефона — альтернатива имени (точнее; неизвестный = reject). */
  phoneSerial?: string;
  modelName?: string;
}

export interface BulkImportResult {
  created: Account[];
  rejected: { index: number; username?: string; error: string }[];
}

// -------- Дашборд / графики --------

export interface DashboardStats {
  totalAccounts: number;
  byStatus: Record<string, number>;
  bansToday: number;
  views24h: number;
  viewsTotal: number;
  /** Самый свежий парсинг по всей ферме — «обновлено» у просмотров. */
  lastParsedAt: string | null;
  phoneCount: number;
  modelCount: number;
  recentEvents: AccountEvent[];
}

export interface ChartPoint {
  date: string; // YYYY-MM-DD
  views24h: number;
  viewsTotal: number;
  accounts: number; // сколько акков дало снапшот в этот день
}

export type ChartRange = '7d' | '1m' | '3m' | 'all';

export const CHART_RANGES: ChartRange[] = ['7d', '1m', '3m', 'all'];

/**
 * Дата «отчёта с 10 утра» (Europe/Warsaw): отчёты приходят ровно в 10:00,
 * поэтому после 10 утра это сегодняшняя дата, до — вчерашняя. Нужна для
 * таймфрейма «С 10 утра»: сравнивать просмотры с утреннего отчёта при
 * подсчёте конверта в фанов в течение дня.
 */
export function since10ReportDate(now: Date = new Date()): string {
  const warsaw = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Warsaw' }));
  if (warsaw.getHours() < 10) warsaw.setDate(warsaw.getDate() - 1);
  const y = warsaw.getFullYear();
  const m = String(warsaw.getMonth() + 1).padStart(2, '0');
  const d = String(warsaw.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// -------- Admin --------

export interface AdminIngestToken {
  id: string;
  name: string;
  prefix: string;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface AdminIngestTokenCreated extends AdminIngestToken {
  rawToken: string;
}

// -------- Ingest (парсер → CRM) --------

/**
 * Один отчёт метрик по аккаунту от внешнего софта.
 * Матчинг по username. Все поля кроме username опциональны.
 */
export interface IngestMetricsReport {
  username: string;
  date?: string; // YYYY-MM-DD, по умолчанию — сегодня
  views24h?: number | null;
  videos24h?: number | null;
  viewsTotal?: number | null;
  viewsCategory?: string | null;
}

/** Аккаунт в выдаче GET /api/ingest/accounts (список на парсинг). */
export interface IngestAccountRow {
  id: string;
  username: string;
  igUrl: string | null;
  containerId: number | null;
  phone: string;
  model: string | null;
  status: AccountStatus | null;
  warn: boolean;
  /** Варн за 18+ контент — отдельный флаг, ставится парсером или руками. */
  warn18: boolean;
  poured: boolean;
  link: string | null;
  viewsCategory: string | null;
  views24h: number;
  videos24h: number;
  viewsTotal: number;
  lastParsedAt: string | null; // ISO
  // Креды — только при ?creds=1
  password?: string | null;
  totpSecret?: string | null;
  email?: string | null;
  emailPassword?: string | null;
}

/** Один рилс в отчёте парсера. */
export interface IngestReelRow {
  url: string;
  publishedAt?: string | null; // YYYY-MM-DD
  caption?: string | null;
  views?: number | null;
  likes?: number | null;
  comments?: number | null;
}

/**
 * Полный результат парсинга одного аккаунта
 * (POST /api/ingest/parse-results, батчем в reports[]).
 */
export interface IngestParseResult {
  username: string;
  date?: string; // YYYY-MM-DD отчёта, по умолчанию сегодня
  /** Парсер может перевести статус (например, обнаружил бан). */
  status?: AccountStatus | null;
  /** Парсер может выставить/снять варн. */
  warn?: boolean | null;
  views24h?: number | null;
  videos24h?: number | null;
  /** Если не передан, а рилсы есть — посчитается как сумма их просмотров. */
  viewsTotal?: number | null;
  viewsCategory?: string | null;
  reels?: IngestReelRow[];
}
